import { useState } from 'react';
import * as Yup from 'yup';
import { Path, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAtomValue } from 'jotai';
import {
  ACCESS_SPACE_UOM,
  CAPACITY_UOM,
  CURRENCY,
  FACILITIES,
  SERVICE_TIME_UNITS,
  SQUARE_ACCESS_SPACE_UOM,
  STANDARD_BUSINESS_HOURS,
  TEMPERATURE_CONTROLS,
} from './constants';
import { General } from './steps/General';
import { WarehouseFormValues } from './types';
import { Facilities } from './steps/Facilities';
import { Services } from './steps/Services';
import { Hours } from './steps/Hours';
import { FormPage } from '../../tailwindComponents/FormPage';
import { businessProfileAtom } from '../../store/jotai';

const dayWorkingHoursSchema = Yup.object({
  working: Yup.boolean(),
  openingTime: Yup.string().when('working', {
    is: true,
    then: (schema) =>
      schema
        .required('Opening time is required')
        .matches(/^\d{2}:\d{2}$/, 'Must be in HH:mm format'),
    otherwise: (schema) => schema.optional(),
  }),
  closingTime: Yup.string().when('working', {
    is: true,
    then: (schema) =>
      schema
        .required('Closing time is required')
        .matches(/^\d{2}:\d{2}$/, 'Must be in HH:mm format'),
    otherwise: (schema) => schema.optional(),
  }),
});

const dayLoadingHoursSchema = Yup.object({
  operating: Yup.boolean(),
  openingTime: Yup.string().when('operating', {
    is: true,
    then: (schema) =>
      schema
        .required('Opening time is required')
        .matches(/^\d{2}:\d{2}$/, 'Must be in HH:mm format'),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  closingTime: Yup.string().when('operating', {
    is: true,
    then: (schema) =>
      schema
        .required('Closing time is required')
        .matches(/^\d{2}:\d{2}$/, 'Must be in HH:mm format'),
    otherwise: (schema) => schema.optional().nullable(),
  }),
});

const optionalNumberSchema = Yup.string()
  .optional()
  .nullable()
  .test(
    'min-if-present',
    'Must be at least 1',
    (value) => !value || Number(value) >= 1
  )
  .matches(/^\d*\.?\d*$/, 'Must be a valid number');

const optionalNumberSchemaAllowZero = Yup.string()
  .optional()
  .nullable()
  .test(
    'min-if-present',
    'Must be at least 0',
    (value) => !value || Number(value) >= 0
  )
  .matches(/^\d*\.?\d*$/, 'Must be a valid number');

const optionalPriceSchema = Yup.string()
  .optional()
  .nullable()
  .test(
    'positive-if-present',
    'Must be greater than 0',
    (value) => !value || Number(value) > 0
  )
  .matches(/^\d*\.?\d*$/, 'Must be a valid number');

const minArraySelectSchema = (min: number, message: string) =>
  Yup.array()
    .of(
      Yup.object({
        label: Yup.string().required(),
        value: Yup.string().required(),
      })
    )
    .min(min, message);

const schema = [
  Yup.object().shape({
    name: Yup.string().required('Name is required'),
    constructionYear: optionalNumberSchema,
    address: Yup.string()
      .required('Warehouse address is required')
      .min(3, 'Warehouse address has to be at least 3 characters long')
      .max(500, 'Warehouse address must be 500 characters or fewer'),
    postcode: Yup.string().required('Postcode is required'),
    businessContactEmail: Yup.string()
      .required('Corporate contact email is required')
      .email('Enter a valid email address'),
    city: Yup.string().nullable(),
    country: Yup.string().nullable(),
    location: Yup.object().shape({
      lon: Yup.number(),
      lat: Yup.number(),
    }),
    experienceSectors: minArraySelectSchema(
      1,
      'At least one experience sector is required'
    ),
    accreditations: minArraySelectSchema(
      1,
      'At least one accreditation is required'
    ),
    accreditationOther: Yup.string().when('accreditations', {
      is: (val: Array<{ value: string }> | undefined) =>
        Array.isArray(val) && val.some((a) => a.value === 'Other'),
      then: (s) => s.required('Please describe the other accreditation'),
      otherwise: (s) => s.optional().nullable(),
    }),
    description: Yup.string().optional().nullable(),
    temperatureControl: Yup.object()
      .shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      })
      .required('Temperature control is required'),
    temperatureMonitoring: minArraySelectSchema(
      1,
      'At least one temperature monitoring option is required'
    ),
    systems: minArraySelectSchema(1, 'At least one system is required'),
    safetyAndSecurity: minArraySelectSchema(
      1,
      'At least one safety and security option is required'
    ),
    capacityUnitOfMeasure: Yup.object()
      .shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      })
      .required('Capacity unit of measure is required'),
    maxStorageCapacity: Yup.string()
      .required('Max storage capacity is required')
      .matches(/^\d*$/, 'Must be a number')
      .min(1, 'Must be at least 1'),
    maxFLOXCapacity: Yup.string()
      .required('Max FLOX capacity is required')
      .matches(/^\d*$/, 'Must be a number')
      .min(1, 'Must be at least 1'),
    remainingCapacity: Yup.string()
      .required('Remaining capacity is required')
      .matches(/^\d*$/, 'Must be a number')
      .min(1, 'Must be at least 1'),
    minCapacityRequirement: Yup.string()
      .required('Min capacity requirement is required')
      .matches(/^\d*$/, 'Must be a number')
      .min(1, 'Must be at least 1'),
    termsUnitOfMeasure: Yup.object()
      .shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      })
      .required('Terms unit of measure is required'),
    minTerm: Yup.string()
      .required('Min term is required')
      .matches(/^\d*$/, 'Must be a number')
      .min(1, 'Must be at least 1'),
  }),
  Yup.object().shape({
    hasOnsiteOffice: Yup.boolean(),
    facilities: Yup.object()
      .shape({
        label: Yup.string(),
        value: Yup.string(),
      })
      .optional()
      .nullable(),
    loadingBays: optionalNumberSchema,
    siteOfficeCountryCode: Yup.string().optional().nullable(),
    siteOfficePhoneNumber: Yup.string()
      .optional()
      .nullable()
      .matches(/^\+?[0-9\s-]*$/, 'Must be a valid phone number'),
    specialFacilities: Yup.array()
      .of(
        Yup.object({
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
      )
      .optional(),
    materialHandlingEquipment: Yup.array()
      .of(
        Yup.object({
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
      )
      .optional(),
    grossInternalAreaUnitOfMeasure: Yup.object().shape({
      label: Yup.string(),
      value: Yup.string(),
    }),
    grossInternalArea: optionalNumberSchema,
    yardSpace: optionalNumberSchema,
    yardSpaceUnitOfMeasure: Yup.object().shape({
      label: Yup.string(),
      value: Yup.string(),
    }),
    levelledLoadingDocks: optionalNumberSchema,
    lorryParkingSpaces: optionalNumberSchema,
    minEavesHeight: optionalNumberSchema,
    minEavesHeightUnitOfMeasure: Yup.object().shape({
      label: Yup.string(),
      value: Yup.string(),
    }),
    mezzanineArea: optionalNumberSchemaAllowZero,
    mezzanineAreaUnitOfMeasure: Yup.object().shape({
      label: Yup.string(),
      value: Yup.string(),
    }),
    vehicleAccessHeight: optionalNumberSchema,
    vehicleAccessHeightUnitOfMeasure: Yup.object().shape({
      label: Yup.string(),
      value: Yup.string(),
    }),
    doubleHeightDocks: optionalNumberSchema,
    carParkingSpaces: optionalNumberSchema,
    rackingAvailable: Yup.boolean(),
    individualRackHeight: Yup.string().when('rackingAvailable', {
      is: true,
      then: ($schema) =>
        $schema
          .required('Individual rack height is required')
          .matches(/^\d*$/, 'Must be a number')
          .min(1, 'Must be at least 1'),
      otherwise: ($schema) => $schema.optional().nullable(),
    }),
    individualRackWidth: Yup.string().when('rackingAvailable', {
      is: true,
      then: ($schema) =>
        $schema
          .required('Individual rack width is required')
          .matches(/^\d*$/, 'Must be a number')
          .min(1, 'Must be at least 1'),
      otherwise: ($schema) => $schema.optional().nullable(),
    }),
    individualRackDepth: Yup.string().when('rackingAvailable', {
      is: true,
      then: ($schema) =>
        $schema
          .required('Individual rack depth is required')
          .matches(/^\d*$/, 'Must be a number')
          .min(1, 'Must be at least 1'),
      otherwise: ($schema) => $schema.optional().nullable(),
    }),
    totalRackHeight: Yup.string().when('rackingAvailable', {
      is: true,
      then: ($schema) =>
        $schema
          .required('Total rack height is required')
          .matches(/^\d*$/, 'Must be a number')
          .min(1, 'Must be at least 1'),
      otherwise: ($schema) => $schema.optional().nullable(),
    }),
  }),
  Yup.object().shape({
    warehouseOperations: minArraySelectSchema(
      1,
      'At least one warehouse operation is required'
    ),
    fulfilmentServices: minArraySelectSchema(
      1,
      'At least one fulfilment service is required'
    ),
    additionalServices: Yup.array()
      .of(
        Yup.object({
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
      )
      .optional(),
    currency: Yup.object()
      .shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      })
      .required('Currency is required'),
    receiptAndPutaway: optionalPriceSchema,
    palletPickingAndDispatch: optionalPriceSchema,
    layerPickingAndDispatch: optionalPriceSchema,
    casePickingAndDispatch: optionalPriceSchema.when(
      'subsequentCasePickingAndDispatch',
      {
        is: (value?: string | null) => !!value,
        then: (s) =>
          s.required(
            'Case picking & despatch price is required when a subsequent price is set'
          ),
      }
    ),
    subsequentCasePickingAndDispatch: optionalPriceSchema,
    itemPickingAndDispatch: optionalPriceSchema.when(
      'subsequentItemPickingAndDispatch',
      {
        is: (value?: string | null) => !!value,
        then: (s) =>
          s.required(
            'Item picking & despatch price is required when a subsequent price is set'
          ),
      }
    ),
    subsequentItemPickingAndDispatch: optionalPriceSchema,
    auxiliaryServices: optionalPriceSchema,
    demurrageCharges: optionalPriceSchema,
    storage: Yup.string()
      .matches(/^\d*\.?\d*$/, 'Must be a valid number')
      .test(
        'positive-if-present',
        'Must be greater than 0',
        (value) => !value || Number(value) > 0
      ),
    containerHandling: optionalPriceSchema,
    palletReturns: optionalPriceSchema,
    caseReturns: optionalPriceSchema,
    itemReturns: optionalPriceSchema,
    surcharges: optionalPriceSchema,
    provisionOfPallet: optionalPriceSchema,
    extraInfo: Yup.string().optional().nullable(),
    haveOwnFleet: Yup.boolean(),
    articsOver26t: optionalNumberSchema,
    rigidsUnder18t: optionalNumberSchema,
    ldvsUnder3_5t: optionalNumberSchema,
    vansUnder1_5t: optionalNumberSchema,
    rigidsUnder26t: optionalNumberSchema,
    rigidsUnder7_5t: optionalNumberSchema,
    specialOther: optionalNumberSchema,
  }),
  Yup.object().shape({
    schedule: Yup.object()
      .shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      })
      .required('Schedule is required'),
    shifts: optionalNumberSchema,
    cutOffTime: Yup.string().optional().nullable(),
    maximumHeadcount: optionalNumberSchema,
    minimumHeadcount: optionalNumberSchema,
    flexibleHoursOnDemand: Yup.boolean(),
    mondayWorkingHours: dayWorkingHoursSchema,
    tuesdayWorkingHours: dayWorkingHoursSchema,
    wednesdayWorkingHours: dayWorkingHoursSchema,
    thursdayWorkingHours: dayWorkingHoursSchema,
    fridayWorkingHours: dayWorkingHoursSchema,
    saturdayWorkingHours: dayWorkingHoursSchema,
    sundayWorkingHours: dayWorkingHoursSchema,
    mondayLoadingHours: dayLoadingHoursSchema,
    tuesdayLoadingHours: dayLoadingHoursSchema,
    wednesdayLoadingHours: dayLoadingHoursSchema,
    thursdayLoadingHours: dayLoadingHoursSchema,
    fridayLoadingHours: dayLoadingHoursSchema,
    saturdayLoadingHours: dayLoadingHoursSchema,
    sundayLoadingHours: dayLoadingHoursSchema,
  }),
];

export function WarehouseForm({
  onSubmit,
  onSaveDraft,
  isSubmitting,
  isSavingDraft,
  header,
  pages,
  initialValues,
  submitError,
  onDismissError,
}: {
  onSubmit: (data: WarehouseFormValues) => void;
  onSaveDraft?: (data: WarehouseFormValues) => void;
  isSubmitting: boolean;
  isSavingDraft?: boolean;
  header: string;
  pages: { name: string; href: string; current: boolean }[];
  initialValues?: WarehouseFormValues;
  submitError?: string | null;
  onDismissError?: () => void;
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const businessProfile = useAtomValue(businessProfileAtom);

  const steps = ['General', 'Facilities', 'Services', 'Hours'];

  const defaultFormValues: WarehouseFormValues = {
    name: '',
    constructionYear: '',
    address: '',
    postcode: '',
    city: '',
    country: '',
    businessContactEmail: businessProfile?.contactEmail ?? '',
    location: {
      lon: 0,
      lat: 0,
    },
    images: [],
    warehouseImages: [],
    deletedImagesIds: [],
    experienceSectors: [],
    accreditations: [],
    accreditationOther: '',
    description: '',
    temperatureControl: TEMPERATURE_CONTROLS[0],
    temperatureMonitoring: [],
    systems: [],
    safetyAndSecurity: [],
    capacityUnitOfMeasure: CAPACITY_UOM[0],
    maxStorageCapacity: '',
    maxFLOXCapacity: '',
    remainingCapacity: '',
    minCapacityRequirement: '',
    termsUnitOfMeasure: SERVICE_TIME_UNITS[0],
    minTerm: '',
    facilities: FACILITIES[0],
    loadingBays: '',
    hasOnsiteOffice: false,
    siteOfficeCountryCode: 'AF',
    siteOfficePhoneNumber: '',
    specialFacilities: [],
    materialHandlingEquipment: [],
    grossInternalArea: '',
    grossInternalAreaUnitOfMeasure: SQUARE_ACCESS_SPACE_UOM[0],
    yardSpace: '',
    yardSpaceUnitOfMeasure: SQUARE_ACCESS_SPACE_UOM[0],
    levelledLoadingDocks: '',
    lorryParkingSpaces: '',
    minEavesHeight: '',
    minEavesHeightUnitOfMeasure: ACCESS_SPACE_UOM[0],
    mezzanineArea: '',
    mezzanineAreaUnitOfMeasure: SQUARE_ACCESS_SPACE_UOM[0],
    vehicleAccessHeight: '',
    vehicleAccessHeightUnitOfMeasure: ACCESS_SPACE_UOM[0],
    doubleHeightDocks: '',
    carParkingSpaces: '',
    rackingAvailable: false,
    individualRackHeight: '',
    individualRackWidth: '',
    individualRackDepth: '',
    totalRackHeight: '',
    warehouseOperations: [],
    fulfilmentServices: [],
    additionalServices: [],
    currency: CURRENCY[0],
    receiptAndPutaway: '',
    palletPickingAndDispatch: '',
    layerPickingAndDispatch: '',
    casePickingAndDispatch: '',
    subsequentCasePickingAndDispatch: '',
    itemPickingAndDispatch: '',
    subsequentItemPickingAndDispatch: '',
    auxiliaryServices: '',
    demurrageCharges: '',
    storage: '',
    containerHandling: '',
    palletReturns: '',
    caseReturns: '',
    itemReturns: '',
    surcharges: '',
    provisionOfPallet: '',
    extraInfo: '',
    haveOwnFleet: false,
    articsOver26t: '',
    rigidsUnder18t: '',
    ldvsUnder3_5t: '',
    vansUnder1_5t: '',
    rigidsUnder26t: '',
    rigidsUnder7_5t: '',
    specialOther: '',
    schedule: STANDARD_BUSINESS_HOURS[0],
    shifts: '',
    cutOffTime: '',
    maximumHeadcount: '',
    minimumHeadcount: '',
    flexibleHoursOnDemand: false,
    mondayWorkingHours: {
      working: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    tuesdayWorkingHours: {
      working: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    wednesdayWorkingHours: {
      working: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    thursdayWorkingHours: {
      working: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    fridayWorkingHours: {
      working: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    saturdayWorkingHours: {
      working: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    sundayWorkingHours: {
      working: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    mondayLoadingHours: {
      operating: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    tuesdayLoadingHours: {
      operating: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    wednesdayLoadingHours: {
      operating: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    thursdayLoadingHours: {
      operating: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    fridayLoadingHours: {
      operating: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    saturdayLoadingHours: {
      operating: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    sundayLoadingHours: {
      operating: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
  };

  const methods = useForm({
    shouldUnregister: false,
    resolver: yupResolver(schema[currentStep - 1]),
    mode: 'onChange',
    defaultValues: initialValues ?? defaultFormValues,
  });

  const { formState, control, setValue } = methods;

  const formSteps = [
    <General
      key={0}
      control={control}
      formState={formState}
      setValue={setValue}
    />,
    <Facilities key={1} control={control} formState={formState} />,
    <Services key={2} control={control} formState={formState} />,
    <Hours key={3} control={control} formState={formState} />,
  ];

  const stepFields: Record<number, Path<WarehouseFormValues>[]> = {
    1: [
      'name',
      'address',
      'postcode',
      'businessContactEmail',
      'experienceSectors',
      'accreditations',
      'accreditationOther',
      'temperatureControl',
      'temperatureMonitoring',
      'systems',
      'safetyAndSecurity',
      'capacityUnitOfMeasure',
      'maxStorageCapacity',
      'maxFLOXCapacity',
      'remainingCapacity',
      'minCapacityRequirement',
      'termsUnitOfMeasure',
      'minTerm',
      'constructionYear',
    ],
    2: [
      'facilities',
      'loadingBays',
      'specialFacilities',
      'materialHandlingEquipment',
      'lorryParkingSpaces',
      'grossInternalArea',
      'yardSpace',
      'levelledLoadingDocks',
      'minEavesHeight',
      'mezzanineArea',
      'vehicleAccessHeight',
      'doubleHeightDocks',
      'carParkingSpaces',
    ],
    3: [
      'warehouseOperations',
      'fulfilmentServices',
      'additionalServices',
      'currency',
      'receiptAndPutaway',
      'palletPickingAndDispatch',
      'layerPickingAndDispatch',
      'casePickingAndDispatch',
      'subsequentCasePickingAndDispatch',
      'itemPickingAndDispatch',
      'subsequentItemPickingAndDispatch',
      'auxiliaryServices',
      'demurrageCharges',
      'storage',
      'containerHandling',
      'palletReturns',
      'caseReturns',
      'itemReturns',
      'surcharges',
      'provisionOfPallet',
      'extraInfo',
    ],
  };

  return (
    <FormPage<WarehouseFormValues>
      header={header}
      steps={steps}
      pages={pages}
      methods={methods}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      onSubmit={onSubmit}
      onSaveDraft={onSaveDraft}
      isSubmitting={isSubmitting}
      isSavingDraft={isSavingDraft}
      stepFields={stepFields}
      submitError={submitError}
      onDismissError={onDismissError}
    >
      {formSteps[currentStep - 1]}
    </FormPage>
  );
}
